"use client";

export type GlowOrbState =
  | "ready"
  | "working"
  | "complete"
  | "waiting"
  | "paused";

type GlowOrbProps = {
  state?: GlowOrbState;
  size?: "sm" | "md" | "lg";
  spinning?: boolean;
  label?: string;
};

const sizeMap = {
  sm: {
    wrap: "h-16 w-16",
    core: "h-9 w-9",
    orbit: "h-14 w-14",
    outerOrbit: "h-16 w-16",
    satellite: "h-3.5 w-3.5",
  },
  md: {
    wrap: "h-24 w-24",
    core: "h-14 w-14",
    orbit: "h-20 w-20",
    outerOrbit: "h-24 w-24",
    satellite: "h-4 w-4",
  },
  lg: {
    wrap: "h-32 w-32",
    core: "h-20 w-20",
    orbit: "h-28 w-28",
    outerOrbit: "h-32 w-32",
    satellite: "h-5 w-5",
  },
} as const;

const stateLabels: Record<GlowOrbState, string> = {
  ready: "Glow is ready",
  working: "Glow is working",
  complete: "Glow completed new work",
  waiting: "Glow is waiting for you",
  paused: "Glow is paused",
};

const stateOrbitDuration: Record<GlowOrbState, string> = {
  ready: "12s",
  working: "3.2s",
  complete: "2.2s",
  waiting: "16s",
  paused: "0s",
};

export default function GlowOrb({
  state = "ready",
  size = "md",
  spinning,
  label,
}: GlowOrbProps) {
  const styles = sizeMap[size];

  const isPaused = state === "paused";
  const isWaiting = state === "waiting";
  const isComplete = state === "complete";
  const isWorking = state === "working";

  const shouldSpin =
    typeof spinning === "boolean"
      ? spinning
      : !isPaused;

  const orbitDuration =
    typeof spinning === "boolean"
      ? spinning
        ? "6s"
        : "0s"
      : stateOrbitDuration[state];

  const accessibleLabel = label || stateLabels[state];

  return (
    <div
      className={`relative flex shrink-0 items-center justify-center ${styles.wrap}`}
      aria-label={accessibleLabel}
      role="img"
      title={accessibleLabel}
    >
      <div
        className={`absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(97,230,220,0.20)_0%,rgba(97,230,220,0.10)_35%,rgba(9,39,35,0)_75%)] ${
          isWorking || isComplete
            ? "motion-safe:animate-pulse"
            : ""
        }`}
      />

      <div
        className={`absolute rounded-full border ${styles.orbit} ${
          isWaiting
            ? "border-[#ffd08f]/45"
            : isPaused
              ? "border-[#9fb2ad]/30"
              : "border-[#61e6dc]/35"
        }`}
      />

      <div
        className={`absolute rounded-full border ${styles.outerOrbit} ${
          isPaused
            ? "border-[#9fb2ad]/20"
            : "border-[#61e6dc]/20"
        }`}
      />

      <div
        className={`relative z-10 rounded-full shadow-[0_0_30px_rgba(97,230,220,0.45)] ${styles.core} ${
          isPaused
            ? "bg-[#52635f]"
            : isWaiting
              ? "bg-[radial-gradient(circle_at_35%_35%,#fff3d6_0%,#ffd08f_35%,#ff9f68_68%,#ff714d_100%)]"
              : "bg-[radial-gradient(circle_at_35%_35%,#dffffa_0%,#8bf3ec_35%,#38d2c5_68%,#149c98_100%)]"
        } ${isWorking || isComplete ? "motion-safe:animate-pulse" : ""}`}
      >
        <div
          className={`absolute inset-[22%] rounded-full ${
            isPaused ? "bg-white/25" : "bg-white/55"
          }`}
        />
      </div>

      <div
        className={`absolute ${styles.orbit} ${
          shouldSpin
            ? "motion-safe:animate-spin"
            : ""
        }`}
        style={{
          animationDuration: orbitDuration,
          animationTimingFunction: "linear",
          animationIterationCount: shouldSpin ? "infinite" : undefined,
        }}
      >
        <div
          className={`absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full border ${styles.satellite} ${
            isPaused
              ? "border-[#d6dfdc] bg-[#a9b8b3]"
              : isWaiting
                ? "border-[#fff1c8] bg-[#ffe5b8] shadow-[0_0_14px_rgba(255,191,120,0.85)]"
                : isComplete
                  ? "border-white bg-[#dffffa] shadow-[0_0_18px_rgba(97,230,220,0.95)]"
                  : "border-white bg-[#d9dfdf] shadow-[0_0_14px_rgba(97,230,220,0.75)]"
          }`}
        />
      </div>
    </div>
  );
}
